import { Fragment, useMemo, useState } from 'react'

import { ANALYTICS_EVENT, GAME_CARD_OFFER_TYPE } from 'constants/index'
import { formatNumber, sendGA4Event } from 'utils/utils'

import useOfferClick from '../useOfferClick'
import SortOffers from './SortOffers'
import useOfferImpression, { offerTileData } from './useOfferImpression'
import { getOfferMilestones, hasCreationDates, OFFER_SORT, sortOffers } from './offerRewards'

/** How many offers are listed before the user asks for the rest. */
const INITIAL_VISIBLE = 4

const AllOffersRow = ({ offer, index, observer, experiment }) => {
  const { ref: impressionRef, guid: offerTileViewGUID } = useOfferImpression(observer)

  const { handleClick, isDisabled } = useOfferClick({
    offer,
    index,
    type: GAME_CARD_OFFER_TYPE.DEFAULT,
    offerTileViewGUID,
    experiment,
  })

  const milestones = getOfferMilestones(offer)
  const totalPiggyBanks = milestones.reduce((total, milestone) => total + milestone.piggyBanks, 0)
  const firstMilestone = milestones[0]

  return (
    <li
      ref={impressionRef}
      data-offer={offerTileData({
        offer,
        index,
        guid: offerTileViewGUID,
        category: 'All offers',
        categoryRowNumber: 0,
      })}
      className={`gamesListCard gamesRedesignOfferRow ${isDisabled ? 'disabled' : ''} ${
        index === 0 ? 'gamesRedesignOnboardingTarget' : ''
      }`}
    >
      <button type="button" className="gamesRedesignOfferRowButton" onClick={handleClick}>
        <img className="gamesRedesignOfferThumb" src={offer.iconUrl} alt="" />
        <span className="gamesRedesignOfferDetails">
          <span className="gamesRedesignOfferTopLine">
            <span className="gamesRedesignOfferName">{offer.gameName}</span>
            {totalPiggyBanks > 0 && (
              <span className="gamesRedesignOfferTotal">
                {formatNumber(totalPiggyBanks)}
                <img src="/images/pig.png" alt="piggy banks" width={16} height={16} />
              </span>
            )}
          </span>
          {firstMilestone?.piggyBanks > 0 && (
            <span className="gamesRedesignOfferFirstReward">
              {formatNumber(firstMilestone.piggyBanks)}
              <img src="/images/pig.png" alt="piggy banks" width={11} height={11} />
              on your first task
            </span>
          )}
        </span>
      </button>
    </li>
  )
}

const AllOffers = ({ offers, observer, experiment }) => {
  const [isExpanded, setExpanded] = useState(false)
  const [sort, setSort] = useState(OFFER_SORT.RECOMMENDED)

  const sortedOffers = useMemo(() => sortOffers(offers || [], sort), [offers, sort])

  if (!offers?.length) {
    return null
  }

  const visibleOffers = isExpanded ? sortedOffers : sortedOffers.slice(0, INITIAL_VISIBLE)
  const hiddenCount = sortedOffers.length - visibleOffers.length

  const handleSort = (nextSort) => {
    setSort(nextSort)
    sendGA4Event(ANALYTICS_EVENT.ALL_OFFERS_SORT, { sort: nextSort, ...experiment })
  }

  const handleShowAll = () => {
    setExpanded(true)
    sendGA4Event(ANALYTICS_EVENT.ALL_OFFERS_SHOW_ALL, {
      hidden_offers: hiddenCount,
      ...experiment,
    })
  }

  return (
    <section className="gamesRedesignAllOffers">
      <header className="gamesRedesignSectionHeader gamesRedesignAllOffersHeader">
        <h2 className="gamesRedesignSectionTitle">All offers</h2>
        <SortOffers sort={sort} onChange={handleSort} canSortByNewest={hasCreationDates(offers)} />
      </header>
      <ul className="gamesRedesignOfferList" data-testid="all-offers-list">
        {visibleOffers.map((offer, index) => (
          <Fragment key={offer.id}>
            {/* Marks where the offers revealed by "Show all" begin. */}
            {index === INITIAL_VISIBLE && (
              <li className="gamesRedesignMoreGames" role="separator">
                More games
              </li>
            )}
            <AllOffersRow offer={offer} index={index} observer={observer} experiment={experiment} />
          </Fragment>
        ))}
      </ul>
      {hiddenCount > 0 && (
        <button type="button" className="gamesRedesignShowAll" onClick={handleShowAll}>
          Show all games ({hiddenCount} more)
        </button>
      )}
    </section>
  )
}

export default AllOffers
